// Design tokens. Every color, font and radius the UI uses lives here and is
// exposed as a CSS variable (camelCase key -> --kebab-case variable).
// Re-brand in one line: change `defaultTheme`, or pass ?theme=sage|mono.
//
// Token names are shared by every preset. In "signature":
//   accent = gold (#F4A323), accentText = goldText (#9A5F00), support = sage (#B2D7B2).

export const defaultTheme = "signature";

const shared = {
  ink: "#231F20",
  paper: "#FFFFFF",
  graphite: "#666263",
  onDarkMuted: "#B5B2B3",
  hairline: "#E2E2E2",
  hairlineDark: "#3D3839",
  inkRaised: "#2E292A",
  chartMuted: "#CFCDCE",
  navBg: "rgba(255, 255, 255, 0.72)",
  logoGold: "#F4A323",
  mapPin: "#DB4437", // Google My Maps red, used for "Your home" pins in map legends

  fontDisplay: '"Montserrat", "Helvetica Neue", Arial, sans-serif',
  fontText: '-apple-system, BlinkMacSystemFont, "SF Pro Text", "Inter", system-ui, sans-serif',

  rLg: "28px",
  rMd: "18px",
  rSm: "12px",
  contentMax: "1120px",
  gutter: "24px",
  gutterWide: "40px",
  sectionPad: "clamp(88px, 12vw, 168px)",
  navHeight: "52px",
  easeOut: "cubic-bezier(0.22, 1, 0.36, 1)",
};

export const themes = {
  signature: {
    ...shared,
    mist: "#F1F2F2",
    accent: "#F4A323",
    accentText: "#9A5F00",
    support: "#B2D7B2",
    onAccent: "#231F20",
    accentOnDark: "#F4A323",
    statusSold: "#231F20",
    statusActive: "#F4A323",
    statusPending: "#B2D7B2",
    subjectRing: "#F4A323",
    accentSoft: "#FDF1DE", // light gold wash behind the recommended service level
  },
  sage: {
    ...shared,
    mist: "#F2F4F1",
    accent: "#B2D7B2",
    accentText: "#4F7F57",
    support: "#F4A323",
    onAccent: "#231F20",
    accentOnDark: "#B2D7B2",
    statusSold: "#231F20",
    statusActive: "#B2D7B2",
    statusPending: "#F4A323",
    subjectRing: "#B2D7B2",
    accentSoft: "#EEF5EE",
  },
  mono: {
    ...shared,
    mist: "#F5F5F7",
    accent: "#231F20",
    accentText: "#231F20",
    support: "#939598",
    onAccent: "#FFFFFF",
    accentOnDark: "#FFFFFF",
    statusSold: "#231F20",
    statusActive: "#939598",
    statusPending: "#C9CACC",
    subjectRing: "#231F20",
    accentSoft: "#F5F5F7",
  },
};

export const themeNames = Object.keys(themes);

const kebab = (s) => s.replace(/[A-Z]/g, (m) => `-${m.toLowerCase()}`);

export function themeToCss() {
  return themeNames
    .map((name) => {
      const vars = Object.entries(themes[name])
        .map(([k, v]) => `--${kebab(k)}:${v};`)
        .join("");
      const selector = name === defaultTheme ? `:root,[data-theme="${name}"]` : `[data-theme="${name}"]`;
      return `${selector}{${vars}}`;
    })
    .join("\n");
}

export function readThemeFromUrl() {
  if (typeof window === "undefined") return defaultTheme;
  const t = new URLSearchParams(window.location.search).get("theme");
  return themeNames.includes(t) ? t : defaultTheme;
}

export function isEditMode() {
  if (typeof window === "undefined") return false;
  return new URLSearchParams(window.location.search).get("edit") === "1";
}

export function installThemeTokens() {
  if (typeof document === "undefined") return;
  let tag = document.getElementById("theme-tokens");
  if (!tag) {
    tag = document.createElement("style");
    tag.id = "theme-tokens";
    document.head.appendChild(tag);
  }
  tag.textContent = themeToCss();
}

export function applyTheme(name) {
  const theme = themeNames.includes(name) ? name : defaultTheme;
  document.documentElement.setAttribute("data-theme", theme);
  window.dispatchEvent(new CustomEvent("themechange", { detail: theme }));
  return theme;
}

// Read a resolved token value (for libraries that need real color strings, e.g. Leaflet).
export function tokenValue(key) {
  if (typeof window === "undefined") return "";
  return getComputedStyle(document.documentElement).getPropertyValue(`--${kebab(key)}`).trim();
}
